import React, { useState } from "react";
import { ArrowUpRight, ExternalLink, Calendar } from "lucide-react";

const SERVICES = [
  {
    title: "Marketing Coaching",
    desc: "1:1 sessions on positioning, channels, customer growth.",
    duration: "60 min · $145",
    url: "https://calendly.com/localizeher/1-hour-marketing-coaching-session",
    available: true,
  },
  {
    title: "Brand Strategy",
    desc: "Identity, voice, and visual system workshop.",
    duration: "90 min · $245",
    url: "https://calendly.com/localizeher/90-minute-brand-strategy-session",
    available: true,
  },
  {
    title: "Fractional CMO Advisory",
    desc: "Senior marketing leadership, monthly retainer. Start with a free discovery call.",
    duration: "30 min · Free discovery",
    url: "https://calendly.com/localizeher/fractional-cmo-discovery-call",
    available: true,
  },
];

export default function Coaching() {
  // The currently-selected service drives the embedded scheduler.
  const [active, setActive] = useState(
    SERVICES.find((s) => s.available) || SERVICES[0]
  );

  return (
    <div data-testid="dashboard-coaching-page">
      <div className="overline text-[var(--lh-mauve)] mb-3">Coaching</div>
      <h1 className="font-serif-display text-5xl tracking-tighter leading-[0.95]">
        Expert eyes on <em>your business.</em>
      </h1>
      <p className="mt-4 text-[var(--lh-text)] font-light max-w-2xl">
        Book directly via Calendly. Payment is collected when you confirm your time.
      </p>

      <div className="mt-12 grid lg:grid-cols-12 gap-6">
        {/* Services list */}
        <div className="lg:col-span-5 space-y-4">
          {SERVICES.map((s) => {
            const isActive = active.title === s.title;
            return (
              <button
                key={s.title}
                type="button"
                disabled={!s.available}
                onClick={() => s.available && setActive(s)}
                className={`w-full text-left p-6 rounded-xl border transition-all ${
                  isActive
                    ? "border-[var(--lh-espresso-noir)] shadow-md"
                    : "border-[var(--lh-border)] hover:border-[var(--lh-cognac)]"
                } ${
                  s.available ? "bg-white cursor-pointer" : "bg-[var(--lh-warm-linen)] opacity-70 cursor-not-allowed"
                }`}
                data-testid={`coaching-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="overline text-[var(--lh-cognac)]">{s.duration}</div>
                  {s.available ? (
                    isActive ? (
                      <span
                        className="text-[0.62rem] uppercase tracking-wider font-bold px-2 py-1 rounded-full"
                        style={{
                          background: "var(--lh-peach)",
                          color: "var(--lh-espresso-noir)",
                        }}
                      >
                        Selected
                      </span>
                    ) : (
                      <span
                        className="text-[0.62rem] uppercase tracking-wider font-semibold"
                        style={{ color: "var(--lh-cognac)" }}
                      >
                        Book →
                      </span>
                    )
                  ) : (
                    <span
                      className="text-[0.62rem] uppercase tracking-wider font-semibold"
                      style={{ color: "var(--lh-greige)" }}
                    >
                      Coming soon
                    </span>
                  )}
                </div>
                <div className="font-serif-display text-2xl">{s.title}</div>
                <p className="text-sm text-[var(--lh-text)] font-light mt-2">
                  {s.desc}
                </p>
              </button>
            );
          })}
        </div>

        {/* Calendly embed */}
        <div className="lg:col-span-7 bg-white border border-[var(--lh-border)] p-6 rounded-xl">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline text-[var(--lh-cognac)] mb-2">
                Book a session
              </div>
              <div className="font-serif-display text-2xl">
                {active.title}
              </div>
              <div
                className="overline mt-1"
                style={{ color: "var(--lh-greige)" }}
              >
                {active.duration}
              </div>
            </div>
            {active.available && active.url && (
              <a
                href={active.url}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                data-testid="coaching-open-calendly"
              >
                Open in Calendly <ExternalLink size={14} />
              </a>
            )}
          </div>

          {active.available && active.url ? (
            <div
              className="rounded-xl overflow-hidden"
              style={{ background: "#FFFFFF", minHeight: 720 }}
              data-testid="calendly-embed"
            >
              <iframe
                src={`${active.url}?hide_gdpr_banner=1&primary_color=FFA873&background_color=FFFFFF&text_color=1A1714`}
                title={`Book ${active.title} on Calendly`}
                width="100%"
                height="720"
                frameBorder="0"
                style={{ minHeight: 720, border: 0, background: "#FFFFFF" }}
              />
            </div>
          ) : (
            <div
              className="aspect-[4/3] rounded-xl flex items-center justify-center text-center"
              style={{ background: "var(--lh-warm-linen)" }}
              data-testid="calendly-placeholder"
            >
              <div className="px-6">
                <Calendar
                  size={32}
                  className="mx-auto mb-4"
                  style={{ color: "var(--lh-cognac)" }}
                  aria-hidden="true"
                />
                <div className="overline text-[var(--lh-cognac)] mb-3">
                  Coming soon
                </div>
                <div className="font-serif-display text-3xl mb-2">
                  {active.title} bookings open soon.
                </div>
                <p className="text-sm text-[var(--lh-muted)] max-w-md mx-auto">
                  In the meantime, the <em>Marketing Coaching</em> session is
                  live and bookable on the left.
                </p>
                <button
                  type="button"
                  onClick={() => setActive(SERVICES[0])}
                  className="btn-primary mt-6"
                >
                  Switch to Marketing Coaching <ArrowUpRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
